import React from 'react';
import {
  CreditCard,
  Sparkles,
  RotateCcw,
  TrendingUp,
  Banknote,
  CircleCheck,
  Clock,
  Hourglass,
  CircleX,
} from 'lucide-react';
import { LoadMainIcon } from './Order';
import type {
  TransactionCustomerCategory,
  TransactionCarrierCategory,
} from '@hero/api';
import { getIconProps, IconProps } from '../utils/props';
import type { TransactionStatus } from '@hero/schema';

//* Customer category icons

export const TransactionPaymentsIcon: React.FC<IconProps> = props => {
  return <CreditCard {...getIconProps(props)} />;
};

export const TransactionOrdersIcon: React.FC<IconProps> = LoadMainIcon;

export const TransactionCashbackIcon: React.FC<IconProps> = props => {
  return <Sparkles {...getIconProps(props)} />;
};

export const TransactionRefundIcon: React.FC<IconProps> = props => {
  return <RotateCcw {...getIconProps(props)} />;
};

//* Carrier category icons

export const TransactionEarningsIcon: React.FC<IconProps> = props => {
  return <TrendingUp {...getIconProps(props)} />;
};

export const TransactionPayoutIcon: React.FC<IconProps> = props => {
  return <Banknote {...getIconProps(props)} />;
};

//* Transaction status icons

export const TransactionStatusSuccessIcon: React.FC<IconProps> = props => {
  return <CircleCheck {...getIconProps(props)} />;
};

export const TransactionStatusPendingIcon: React.FC<IconProps> = props => {
  return <Clock {...getIconProps(props)} />;
};

export const TransactionStatusWaitingIcon: React.FC<IconProps> = props => {
  return <Hourglass {...getIconProps(props)} />;
};

export const TransactionStatusFailedIcon: React.FC<IconProps> = props => {
  return <CircleX {...getIconProps(props)} />;
};

//* Dynamic icons

const CUSTOMER_CATEGORY_ICONS: Record<
  TransactionCustomerCategory,
  React.FC<IconProps>
> = {
  payments: TransactionPaymentsIcon,
  orders: TransactionOrdersIcon,
  cashbacks: TransactionCashbackIcon,
  refunds: TransactionRefundIcon,
};

const CARRIER_CATEGORY_ICONS: Record<
  TransactionCarrierCategory,
  React.FC<IconProps>
> = {
  earnings: TransactionEarningsIcon,
  payouts: TransactionPayoutIcon,
  cashbacks: TransactionCashbackIcon,
  refunds: TransactionRefundIcon,
};

export const TransactionCategoryDynamicIcon: React.FC<
  IconProps & {
    category: TransactionCustomerCategory | TransactionCarrierCategory;
    displayForUser: 'customer' | 'carrier';
  }
> = ({ category, displayForUser, ...rest }) => {
  const icons =
    displayForUser === 'customer' ? CUSTOMER_CATEGORY_ICONS : CARRIER_CATEGORY_ICONS;
  const Icon = (icons as Record<string, React.FC<IconProps>>)[category];
  if (!Icon) return <TransactionPaymentsIcon {...rest} />;
  return <Icon {...rest} />;
};

const STATUS_ICONS: Record<TransactionStatus, React.FC<IconProps>> = {
  success: TransactionStatusSuccessIcon,
  pending: TransactionStatusPendingIcon,
  waiting_for_approval: TransactionStatusWaitingIcon,
  failed: TransactionStatusFailedIcon,
};

export const TransactionStatusDynamicIcon: React.FC<
  IconProps & { status: TransactionStatus }
> = ({ status, ...rest }) => {
  const Icon = STATUS_ICONS[status] ?? TransactionStatusPendingIcon;
  return <Icon {...rest} />;
};
